import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { famousRigs } from '../data/rigsData';
import { Guitar, Headphones, Zap, X, Music2, Disc, ArrowRight } from 'lucide-react';

const FamousRigs = () => {
  const [selectedRig, setSelectedRig] = useState(null);
  const [activeTab, setActiveTab] = useState('gear');

  return (
    <section id="famous-rigs" className="relative py-24 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-950/80"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="h-2 w-2 rounded-full bg-gradient-to-r from-purple-400 to-blue-500 mr-2"></span>
            <span className="text-sm font-medium bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-500">
              Iconic Guitar Tones
            </span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Legendary <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">Guitar Rigs</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Discover the gear behind the most iconic guitar tones in music history.
          </p>
        </motion.div>

        {/* Rigs grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {famousRigs.map((rig, index) => (
            <motion.div
              key={rig.id}
              className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl rounded-2xl p-0.5 overflow-hidden hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 cursor-pointer"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => setSelectedRig(rig)}
              whileHover={{ y: -5 }}
            >
              <div className="relative h-full bg-gradient-to-br from-gray-900 to-gray-800/80 rounded-2xl overflow-hidden">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10"></div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-30">
                    <Music2 className="h-16 w-16 text-purple-400" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                    <h3 className="text-xl font-bold text-white">
                      {rig.artist}
                    </h3>
                    <p className="text-sm text-gray-300">{rig.genre}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-lg bg-white/5">
                        <Guitar className="h-4 w-4 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Guitar</p>
                        <p className="text-white text-sm">{rig.guitar}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-lg bg-white/5">
                        <Headphones className="h-4 w-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Amp</p>
                        <p className="text-white text-sm">{rig.amp}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="p-2 rounded-lg bg-white/5">
                        <Zap className="h-4 w-4 text-yellow-400" />
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Effects</p>
                        <p className="text-white text-sm">{rig.effects.length} pedals</p>
                      </div>
                    </div>
                  </div>

                  <motion.div 
                    className="mt-6 flex items-center gap-1 text-sm font-medium text-purple-400 group-hover:text-purple-300 transition-colors"
                    whileHover={{ x: 5 }}
                  >
                    <span>Explore rig</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedRig && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedRig(null)}
          >
            <motion.div
              className="bg-gradient-to-br from-gray-900 to-gray-800/90 backdrop-blur-xl rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl shadow-purple-500/10"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="p-6 border-b border-white/10">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-3xl font-bold text-white">
                      {selectedRig.artist}
                    </h3>
                    <p className="text-purple-400 mt-1">{selectedRig.genre} • {selectedRig.era}</p>
                  </div>
                  <button
                    onClick={() => setSelectedRig(null)}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors text-gray-300 hover:text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* Modal content */}
              <div className="p-6">
                <p className="text-gray-300 mb-8 leading-relaxed">{selectedRig.description}</p>

                {/* Tabs */}
                <div className="flex border-b border-white/10 mb-6">
                  <button
                    className={`px-4 py-2 font-medium text-sm flex items-center gap-2 ${
                      activeTab === 'gear' 
                        ? 'text-purple-400 border-b-2 border-purple-500' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                    onClick={() => setActiveTab('gear')}
                  >
                    <Guitar className="h-4 w-4" />
                    Gear Setup
                  </button>
                  <button
                    className={`px-4 py-2 font-medium text-sm flex items-center gap-2 ${
                      activeTab === 'songs' 
                        ? 'text-purple-400 border-b-2 border-purple-500' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                    onClick={() => setActiveTab('songs')}
                  >
                    <Disc className="h-4 w-4" />
                    Famous Songs
                  </button>
                </div>

                {/* Tab content */}
                <AnimatePresence mode="wait">
                  {activeTab === 'gear' ? (
                    <motion.div
                      key="gear"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-6"
                    >
                      <div className="space-y-3">
                        <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                          <Guitar className="h-5 w-5 text-purple-400" />
                          Guitar
                        </h4>
                        <p className="text-gray-300 pl-7">{selectedRig.guitar}</p>
                      </div>
                      
                      <div className="space-y-3">
                        <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                          <Headphones className="h-5 w-5 text-blue-400" />
                          Amplifier
                        </h4>
                        <p className="text-gray-300 pl-7">{selectedRig.amp}</p>
                      </div>
                      
                      <div className="space-y-3">
                        <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                          <Zap className="h-5 w-5 text-yellow-400" />
                          Effects Pedals
                        </h4>
                        <div className="flex flex-wrap gap-2 pl-7">
                          {selectedRig.effects.map((effect, idx) => (
                            <span 
                              key={idx}
                              className="px-3 py-1 rounded-full text-sm bg-white/5 text-gray-300 border border-white/10"
                            >
                              {effect}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="songs"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      <p className="text-gray-400 mb-4">Iconic tracks featuring this rig:</p>
                      <div className="space-y-3">
                        {selectedRig.songs.map((song, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors group"
                          >
                            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center text-purple-400">
                              <Music2 className="h-5 w-5" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-white group-hover:text-purple-300 transition-colors">
                                {song}
                              </div>
                              <div className="text-xs text-gray-400">{selectedRig.artist}</div>
                            </div>
                            <button className="p-2 rounded-full hover:bg-white/10 transition-colors text-gray-400 hover:text-white">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default FamousRigs;
